import { createSelector, createSlice } from "@reduxjs/toolkit";
import { convert } from "../../components/Math/Conversion/convert.js";
import { ImperialUnits, MetricUnits } from "../../components/Math/Units/AllUnits.jsx";
import { CLAY_OR_ROCK, COMPRESSION_PILE, TENSION_PILE } from "../../constants.js";
import { reset } from "../resetParameters.js";
import { selectIsImperial, selectIsMetric, selectMeasurementType } from "./settingsSlice.js";

const initialState = {
  loadType: COMPRESSION_PILE,
  factorOfSafety: 0,
  totalDesignLoad: {
    value: 0,
    unit: ImperialUnits.Kilopound,
  },
  selectedCarryingElement: {
    name: "TITAN 30/11",
  },
  fyControlling: { value: 0, unit: ImperialUnits.KilopoundPerSquareInch },
  py: { value: 0, unit: ImperialUnits.Kilopound },
  fy: { value: 0, unit: ImperialUnits.KilopoundPerSquareInch },
  areaOfSteel: { value: 0, unit: ImperialUnits.InchSquared },
  barOD: { value: 0, unit: ImperialUnits.Inch },
  holeSize: {
    value: 0,
    unit: ImperialUnits.Inch,
  },
  holeSizeIsCustom: false,
  groutStrength: {
    value: 5,
    unit: ImperialUnits.KilopoundPerSquareInch,
  },
  safetyFactor: {
    Compression: 0.47,
    CompressionTest: 0.8,

    Tension: 0.55,
    TensionTest: 0.8,

    GroutFactor: 0.4,
    GroutFactorTest: 0.68,
  },
  totalLength: {
    value: 0,
    unit: ImperialUnits.Foot,
  },
  frontendState: {
    calculatedDesignLoadCheckbox: false,
    showBarDataDetails: false,
  },
  extensionIntoFoundation: { value: 0, unit: ImperialUnits.Foot },
  layersInput: [
    {
      index: 0,
      description: "Layer 1",
      widening: { value: CLAY_OR_ROCK, unit: ImperialUnits.Inch },
      layerTop: { value: 0, unit: ImperialUnits.Foot },
      layerBottom: { value: Number.MAX_SAFE_INTEGER, unit: ImperialUnits.Foot },
      skinFriction: { value: 0, unit: ImperialUnits.PoundPerSquaredInch },
      factorOfSafety: 0,
    },
  ],
  pileInclinationFromVertical: 0,
};

const fhwaSlice = createSlice({
  name: "fhwa",
  initialState,
  reducers: {
    setTension(state, action) {
      state.safetyFactor.Tension = action.payload;
    },
    setCompression(state, action) {
      state.safetyFactor.Compression = action.payload;
    },
    setCompressionTest(state, action) {
      state.safetyFactor.CompressionTest = action.payload;
    },
    setTensionTest(state, action) {
      state.safetyFactor.TensionTest = action.payload;
    },
    setGroutFactor(state, action) {
      state.safetyFactor.GroutFactor = action.payload;
    },
    setGroutFactorTest(state, action) {
      state.safetyFactor.GroutFactorTest = action.payload;
    },
    setTotalDesignLoad(state, action) {
      state.totalDesignLoad = action.payload;
    },
    setFactorOfSafety(state, action) {
      state.factorOfSafety = action.payload;
    },
    setCarryingElementName(state, action) {
      state.selectedCarryingElement.name = action.payload;
    },
    setFyControlling(state, action) {
      state.fyControlling = action.payload;
    },
    setPy(state, action) {
      state.py = action.payload;
    },
    setFy(state, action) {
      state.fy = action.payload;
    },
    setAreaOfSteel(state, action) {
      state.areaOfSteel = action.payload;
    },
    setBarOD(state, action) {
      state.barOD = action.payload;
    },

    setHoleSize(state, action) {
      state.holeSize = action.payload;
      state.holeSizeIsCustom = false;
    },
    setHoleSizeCustom(state, action) {
      state.holeSize = action.payload;
      state.holeSizeIsCustom = true;
    },
    setLoadType(state, action) {
      state.loadType = action.payload;
    },
    setGroutStrength(state, action) {
      state.groutStrength = action.payload;
    },
    setExtensionIntoFoundation(state, action) {
      state.extensionIntoFoundation = action.payload;
    },
    setFhwaState(state, action) {
      state.loadType = action.payload.loadType;
      state.factorOfSafety = action.payload.factorOfSafety;
      state.totalDesignLoad = action.payload.totalDesignLoad;
      state.selectedCarryingElement = action.payload.selectedCarryingElement;
      state.fyControlling = action.payload.fyControlling;
      state.py = action.payload.py;
      state.fy = action.payload.fy;
      state.areaOfSteel = action.payload.areaOfSteel;
      state.barOD = action.payload.barOD;
      state.holeSize = action.payload.holeSize;
      state.holeSizeIsCustom = action.payload.holeSizeIsCustom;
      state.groutStrength = action.payload.groutStrength;
      state.safetyFactor = action.payload.safetyFactor;
      state.totalLength = action.payload.totalLength;
      state.frontendState = action.payload.frontendState;
      state.extensionIntoFoundation = action.payload.extensionIntoFoundation;
      state.layersInput = action.payload.layersInput;
      state.pileInclinationFromVertical = action.payload.pileInclinationFromVertical;
    },
    setFrontendState(state, action) {
      state.frontendState = {
        ...state.frontendState,
        ...action.payload,
      };
    },
    addLayer(state, action) {
      state.layersInput.push(action.payload);

      let newLayer = {};
      newLayer.index = action.payload.index;
    },
    deleteLayer(state, action) {
      const { position } = action.payload;
      state.layersInput.splice(position, 1);
    },
    updateLayerValue(state, action) {
      const { index } = action.payload;
      state.layersInput[index] = {
        ...state.layersInput[index],
        ...action.payload,
      };
    },
    setPileInclinationFromVertical(state, action) {
      state.pileInclinationFromVertical = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(reset, (state, action) => {
      return initialState;
    });
  },
});

export const {
  setTension,
  setCompression,
  setTensionTest,
  setCompressionTest,
  setGroutFactor,
  setGroutFactorTest,
  setFhwaState,
  setBarOD,
  setAreaOfSteel,
  setFy,
  setPy,
  setFyControlling,
  setFrontendState,
  setExtensionIntoFoundation,
  setLoadType,
  setCarryingElementName,
  setGroutStrength,
  setTotalDesignLoad,
  setHoleSize,
  setHoleSizeCustom,
  setFactorOfSafety,
  setPileInclinationFromVertical,
  addLayer,
  deleteLayer,
  updateLayerValue,
} = fhwaSlice.actions;

export default fhwaSlice.reducer;

export const selectLayers = (state) => state.fhwa.layersInput;
export const selectLoadType = (state) => state.fhwa.loadType;
export const selectPileInclinationFromVertical = (state) => state.fhwa.pileInclinationFromVertical;
export const selectSelectedCarryingElement = (state) => state.fhwa.selectedCarryingElement;
export const selectHoleSizeIsCustom = (state) => state.fhwa.holeSizeIsCustom;
export const selectSafetyFactor = (state) => state.fhwa.safetyFactor;
export const selectFactorOfSafety = (state) => state.fhwa.factorOfSafety;
export const selectFrontendState = (state) => state.fhwa.frontendState;

export const selectAllInputLayers = createSelector(
  [selectIsImperial, selectIsMetric, selectLayers],
  (isImperial, isMetric, layersInput) => {
    let arr = [];
    layersInput.forEach((layer) => {
      const { index, widening, layerTop, layerBottom, skinFriction, ...rest } = layer;
      arr.push({
        index,
        widening: convert(widening, isImperial, isMetric),
        layerTop: convert(layerTop, isImperial, isMetric),
        layerBottom: convert(layerBottom, isImperial, isMetric),
        skinFriction: convert(
          skinFriction,
          isImperial,
          isMetric,
          ImperialUnits.PoundPerSquaredInch,
          MetricUnits.KiloNewtonDividedByMetreSquared
        ),
        ...rest,
      });
    });
    return arr;
  }
);

export const selectHoleSize = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.holeSize],
  (isImperial, isMetric, holeSize) => {
    return convert(holeSize, isImperial, isMetric);
  }
);

export const selectTotalDesignLoad = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.totalDesignLoad],
  (isImperial, isMetric, totalDesignLoad) => {
    return convert(totalDesignLoad, isImperial, isMetric);
  }
);

export const selectLayersCalculated = createSelector(
  [
    selectIsImperial,
    selectIsMetric,
    selectHoleSize,
    selectAllInputLayers,
    selectPileInclinationFromVertical,
    selectTotalDesignLoad,
  ],
  (isImperial, isMetric, holeSize, layersInput, pileInclinationFromVertical, totalDL) => {
    function degreeToRadians(degree) {
      return (Number(degree) * Math.PI) / 180;
    }

    function calcInclinedLength(from, to, angle) {
      let ilength =
        Number(to.imperial) === Number.MAX_SAFE_INTEGER
          ? Number.MAX_SAFE_INTEGER
          : Math.abs(Number(to.imperial) - Number(from.imperial)) /
            Math.cos(degreeToRadians(Number(angle)));

      return {
        value: ilength,
        unit: ImperialUnits.Foot,
      };
    }

    function calcLoadTaken(widening, allowableSkinFriction, inclinedLength) {
      let loadTaken =
        Number(inclinedLength.imperial) === Number.MAX_SAFE_INTEGER
          ? Number(totalDL.imperial)
          : Math.PI *
            (((Number(holeSize.imperial) + Number(widening.imperial)) * 1) / 12) *
            Number(inclinedLength.imperial) *
            KSF_TO_PSI_FACTOR *
            Number(allowableSkinFriction.imperial);

      return {
        value: loadTaken,
        unit: ImperialUnits.Kilopound,
      };
    }

    function calcAllowableSkinFriction(skinFriction, factorOfSafety) {
      let allowableSkinFriction = {
        value: 0,
        unit: ImperialUnits.PoundPerSquaredInch,
      };
      if (factorOfSafety === 0) return allowableSkinFriction;

      allowableSkinFriction.value = Number(skinFriction.imperial) / Number(factorOfSafety);

      return allowableSkinFriction;
    }

    let arr = [];
    let iLSum = 0;
    layersInput.forEach((el, index) => {
      let inclinedLengthCalc = calcInclinedLength(
        el.layerTop,
        el.layerBottom,
        pileInclinationFromVertical
      );
      let inclinedLength = convert(inclinedLengthCalc, isImperial, isMetric);

      let allowableSkinFrictionCalc = calcAllowableSkinFriction(el.skinFriction, el.factorOfSafety);
      let allowableSkinFriction = convert(
        allowableSkinFrictionCalc,
        isImperial,
        isMetric,
        ImperialUnits.PoundPerSquaredInch,
        MetricUnits.KiloNewtonDividedByMetreSquared
      );

      let loadTakenInLayerCalc = calcLoadTaken(el.widening, allowableSkinFriction, inclinedLength);

      let diff =
        index > 0 && Number(inclinedLength.imperial) === Number.MAX_SAFE_INTEGER
          ? {
              value: Number(totalDL.imperial) - iLSum,
              unit: "Kilopound",
            }
          : loadTakenInLayerCalc;

      let loadTakenInLayer = convert(diff, isImperial, isMetric);

      iLSum += loadTakenInLayerCalc.value;

      arr.push({
        index,
        inclinedLength,
        loadTakenInLayer,
        allowableSkinFriction,
      });
    });

    return arr;
  }
);

export const selectSumLoadTakenInLayerTotal = createSelector(
  [selectIsImperial, selectIsMetric, selectLayersCalculated],
  (isImperial, isMetric, layersCalculated) => {
    let result = layersCalculated.reduce(function (total, layer) {
      return Number(total) + Number(layer.loadTakenInLayer.imperial);
    }, 0);

    var SumLoadTakenInLayer = {
      value: result,
      unit: ImperialUnits.Kilopound,
    };

    return convert(SumLoadTakenInLayer, isImperial, isMetric);
  }
);

export const selectTableUtilization = createSelector(
  [selectSumLoadTakenInLayerTotal, selectTotalDesignLoad],
  (sumLoadTakenInLayerTotal, totalDesignLoad) => {
    if (sumLoadTakenInLayerTotal.current === 0) return 0;
    return Number(totalDesignLoad.current) / Number(sumLoadTakenInLayerTotal.current);
  }
);

const KSF_TO_PSI_FACTOR = 0.144000092;
export const selectInclinedLengthSum = createSelector(
  [selectIsImperial, selectIsMetric, selectLayersCalculated, selectHoleSize, selectAllInputLayers],
  (isImperial, isMetric, layersCalculated, holeSize, inputLayers) => {
    let sum = layersCalculated.reduce(function (total, layer) {
      return Number(layer.inclinedLength.imperial) === Number.MAX_SAFE_INTEGER
        ? Number(total) +
            Number(layer.loadTakenInLayer.imperial) /
              (Math.PI *
                (((Number(holeSize.imperial) + Number(inputLayers[layer.index].widening.imperial)) /
                  12) *
                  KSF_TO_PSI_FACTOR *
                  Number(layer.allowableSkinFriction.imperial)))
        : Number(total) + Number(layer.inclinedLength.imperial);
    }, 0);

    let inclinedLengthSum = {
      value: sum,
      unit: ImperialUnits.Foot,
    };

    return convert(inclinedLengthSum, isImperial, isMetric);
  }
);

export const selectExtensionIntoFoundation = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.extensionIntoFoundation],
  (isImperial, isMetric, extensionIntoFoundation) => {
    return convert(extensionIntoFoundation, isImperial, isMetric);
  }
);

export const selectTotalLength = createSelector(
  [selectIsImperial, selectIsMetric, selectExtensionIntoFoundation, selectInclinedLengthSum],
  (isImperial, isMetric, extensionIntoFoundation, inclinedLengthSum) => {
    let res = Number(extensionIntoFoundation.imperial) + Number(inclinedLengthSum.imperial);

    let totalLength = {
      value: res,
      unit: ImperialUnits.Foot,
    };

    return convert(totalLength, isImperial, isMetric);
  }
);

export const selectUltimateLoad = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.totalDesignLoad, selectFactorOfSafety],
  (isImperial, isMetric, totalDesignLoad, factorOfSafety) => {
    let ultimateLoad = {
      value: Number(totalDesignLoad.value) * Number(factorOfSafety),
      unit: totalDesignLoad.unit,
    };

    return convert(ultimateLoad, isImperial, isMetric);
  }
);

export const selectBarOD = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.barOD],
  (isImperial, isMetric, barOD) => {
    return convert(barOD, isImperial, isMetric);
  }
);

export const selectAreaOfSteel = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.areaOfSteel],
  (isImperial, isMetric, areaOfSteel) => {
    return convert(areaOfSteel, isImperial, isMetric);
  }
);

export const selectFy = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.fy],
  (isImperial, isMetric, fy) => {
    return convert(fy, isImperial, isMetric);
  }
);

export const selectPy = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.py],
  (isImperial, isMetric, py) => {
    return convert(py, isImperial, isMetric);
  }
);

export const selectFyControlling = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.fyControlling],
  (isImperial, isMetric, fyControlling) => {
    return convert(fyControlling, isImperial, isMetric);
  }
);

export const selectGroutStrength = createSelector(
  [selectIsImperial, selectIsMetric, (state) => state.fhwa.groutStrength],
  (isImperial, isMetric, groutStrength) => {
    // HACK: custom metric value
    if (
      isMetric &&
      groutStrength.value == 5 &&
      groutStrength.unit === ImperialUnits.KilopoundPerSquareInch
    ) {
      let result = {
        current: 35,
        imperial: 5,
        metric: 35,
      };
      return result;
    }

    return convert(groutStrength, isImperial, isMetric);
  }
);

export const selectAreaOfGrout = createSelector(
  [selectIsImperial, selectIsMetric, selectAreaOfSteel, selectHoleSize],
  (isImperial, isMetric, areaOfSteel, holeSize) => {
    let areaOfGrout = {
      value: Math.PI * Math.pow(holeSize.imperial / 2, 2) - areaOfSteel.imperial,
      unit: ImperialUnits.InchSquared,
    };
    return convert(areaOfGrout, isImperial, isMetric);
  }
);

export const selectAllowableLoad = createSelector(
  [
    selectIsImperial,
    selectIsMetric,
    selectLoadType,
    selectSafetyFactor,
    selectGroutStrength,
    selectAreaOfGrout,
    selectFyControlling,
    selectAreaOfSteel,
  ],
  (
    isImperial,
    isMetric,
    loadType,
    safetyFactor,
    groutStrength,
    areaOfGrout,
    fyControlling,
    areaOfSteel
  ) => {
    const { Compression, Tension, GroutFactor } = safetyFactor;

    let result;
    if (loadType === COMPRESSION_PILE) {
      result =
        GroutFactor * groutStrength.imperial * areaOfGrout.imperial +
        Compression * fyControlling.imperial * areaOfSteel.imperial;
    }

    if (loadType === TENSION_PILE) {
      result = Tension * areaOfSteel.imperial * fyControlling.imperial;
    }

    let allowableLoad = {
      value: result,
      unit: ImperialUnits.Kilopound,
    };
    return convert(allowableLoad, isImperial, isMetric);
  }
);

export const selectAllowableTestLoad = createSelector(
  [
    selectIsImperial,
    selectIsMetric,
    selectLoadType,
    selectSafetyFactor,
    selectGroutStrength,
    selectAreaOfGrout,
    selectFyControlling,
    selectAreaOfSteel,
  ],
  (
    isImperial,
    isMetric,
    loadType,
    safetyFactor,
    groutStrength,
    areaOfGrout,
    fyControlling,
    areaOfSteel
  ) => {
    const { CompressionTest, TensionTest, GroutFactorTest } = safetyFactor;
    let result =
      loadType === COMPRESSION_PILE
        ? GroutFactorTest * groutStrength.imperial * areaOfGrout.imperial +
          CompressionTest * fyControlling.imperial * areaOfSteel.imperial
        : TensionTest * areaOfSteel.imperial * fyControlling.imperial;

    let allowableLoadTestLoad = {
      value: result,
      unit: ImperialUnits.Kilopound,
    };

    return convert(allowableLoadTestLoad, isImperial, isMetric);
  }
);

export const selectFHWA = createSelector(
  [
    (state) => state.fhwa,
    selectMeasurementType,
    selectLoadType,
    selectTotalDesignLoad,
    selectHoleSize,
    selectFactorOfSafety,
    selectExtensionIntoFoundation,
    selectAllInputLayers,
    selectLayersCalculated,
    selectPileInclinationFromVertical,
    selectBarOD,
    selectAreaOfSteel,
    selectFy,
    selectPy,
    selectFyControlling,
    selectInclinedLengthSum,
    selectTotalLength,
    selectTableUtilization,
    selectSumLoadTakenInLayerTotal,
    selectAllowableLoad,
    selectAllowableTestLoad,
    selectAreaOfGrout,
    selectUltimateLoad,
    selectGroutStrength,
    selectSafetyFactor,
  ],
  (
    state,
    measurementType,
    loadType,
    totalDesignLoad,
    holeSize,
    factorOfSafety,
    extensionIntoFoundation,
    allInputLayers,
    layersCalculated,
    pileInclinationFromVertical,
    barOD,
    areaOfSteel,
    fy,
    py,
    fyControlling,
    inclinedLengthSum,
    totalLength,
    tableUtilization,
    sumLoadTakenInLayerTotal,
    allowableLoad,
    allowableTestLoad,
    areaOfGrout,
    ultimateLoad,
    groutStrength,
    safetyFactor
  ) => {
    // only use state if there is no specific select
    return {
      measurementType: measurementType,
      loadType: loadType,
      totalDesignLoad: totalDesignLoad,
      holeSize: holeSize,
      holeSizeIsCustom: state.holeSizeIsCustom,
      selectedCarryingElement: state.selectedCarryingElement,
      frontendState: state.frontendState,
      factorOfSafety: factorOfSafety,
      extensionIntoFoundation: extensionIntoFoundation,
      layersInput: allInputLayers,
      layersCalculated: layersCalculated,
      pileInclinationFromVertical: pileInclinationFromVertical,
      barOD: barOD,
      areaOfSteel: areaOfSteel,
      fy: fy,
      py: py,
      fyControlling: fyControlling,
      inclinedLengthTableSum: inclinedLengthSum,
      totalLength: totalLength,
      tableUtilization: tableUtilization,
      tableTotal: sumLoadTakenInLayerTotal,
      allowableLoad: allowableLoad,
      allowableTestLoad: allowableTestLoad,
      areaOfGrout: areaOfGrout,
      ultimateLoad: ultimateLoad,
      groutStrength: groutStrength,
      safetyFactor: safetyFactor,
    };
  }
);
